import React, { useMemo } from 'react';
import { useState2 } from '../../util/useState2';
import "./Table.scss";

export interface IColumn<T> {
    header?: React.ReactNode;
    footer?: React.ReactNode;
    key: string;
    content: (t: T, rowIndex: number) => (React.ReactNode);
    className?: string;
    sort?: (a: T, b: T) => number;
}

export function Table<T>(props: {
    columns: IColumn<T>[],
    rows: T[],
    rowKey?: (t: T, index: number) => (string | number),
    tableProps?: React.HTMLProps<HTMLTableElement>
    onRowClick?: (row: T) => void
}) {

    const sortStrategy = useState2<{colIndex: number, reverse: boolean}>();

    const {
        className: originalClassName,
        ...otherTableProps
    } = props.tableProps ?? {};

    const rows = useMemo(() => {
        if (!sortStrategy.value) {
            return props.rows;
        }
        const sorter = props.columns?.[sortStrategy.value.colIndex]?.sort;
        if (!sorter) {
            return props.rows;
        }
            
        const result = [...props.rows]; //copy existing array, because sorting changes the array
        const reverser = sortStrategy.value.reverse ? -1 : 1;
        result.sort((a, b) => reverser * sorter(a, b));
        return result;

    }, [sortStrategy.value, props.rows, props.columns])
    

    const tableClassName = originalClassName ?? "table table-striped table-hover";
    function setSort(colIndex: number) {
        if (!props.columns[colIndex].sort) {
            return; //column is not sortable
        }
        if (colIndex === sortStrategy.value?.colIndex) {
            sortStrategy.set({
                colIndex: colIndex,
                reverse: !sortStrategy.value.reverse
            });
        }
        else {
            sortStrategy.set({
                colIndex: colIndex,
                reverse: false
            })
        }
    }

    function getHeaderClasses(colIndex: number) {
        const col = props.columns[colIndex];
        const results: string[] = [];
        if (col?.className) {
            results.push(col.className);
        }
        if (col?.sort) {
            results.push("sortable-column");
        }
        if (colIndex === sortStrategy.value?.colIndex) {
            results.push("sorted-column");
            if (sortStrategy.value?.reverse) {
                results.push("sorted-column-reverse");
            }
        }

        return results.join(" ");
    }

    const cols = props.columns.map(col => (
        <col key={col.key} className={col.className} />
    ));

    const headers = props.columns.some(c => c.header)
        && props.columns.map((col, colIndex) => (
            <th key={col.key}
                className={getHeaderClasses(colIndex)}
                onClick={() => setSort(colIndex)}
            >
                {col.header}
            </th>
        ));

    const rowElements = rows.map((dat, rowIndex) => {
        const fields = props.columns.map((col, colIndex) => (
            <td key={col.key} className={col.className}>{col.content(dat, rowIndex)}</td>
        ));
        return (
            <tr key={props.rowKey?.(dat, rowIndex) ?? rowIndex}
                onClick={props.onRowClick ? (() => props.onRowClick!(dat)) : undefined}
            >
                {fields}
            </tr>
        );
    });

    const footers = props.columns.some(col => col.footer)
        && props.columns.map(col => (
            <th key={col.key} className={col.className}>{col.footer}</th>
        ));


    return (
        <table {...otherTableProps} className={tableClassName}>
            <colgroup>
                {cols}
            </colgroup>

            {headers && (
                <thead>
                    <tr>
                        {headers}
                    </tr>
                </thead>
            )}

            <tbody>
                {rowElements}
            </tbody>

            {footers && (
                <tfoot>
                    <tr>
                        {footers}
                    </tr>
                </tfoot>
            )}
        </table>
    );
}

